import { createSlice } from "@reduxjs/toolkit";

/**
 * threads[threadKey].activeQueryKey
 * threads[threadKey].entries[queryKey] = { status, statusText, markdown, updatedAt, error }
 */
const initialState = {
    threads: {},
};

const ensureThread = (state, threadKey) => {
    if (!state.threads[threadKey]) {
        state.threads[threadKey] = { activeQueryKey: undefined, entries: {} };
    }
    return state.threads[threadKey];
};

const ensureEntry = (state, threadKey, queryKey) => {
    const thread = ensureThread(state, threadKey);
    if (!thread.entries[queryKey]) {
        thread.entries[queryKey] = {
            status: "idle", // idle | loading | streaming | done | error
            statusText: "",
            markdown: "",
            updatedAt: Date.now(),
            error: "",
        };
    }
    return thread.entries[queryKey];
};

const aiCacheSlice = createSlice({
    name: "aiCache",
    initialState,
    reducers: {
        setActiveQuery(state, action) {
            const { threadKey, queryKey } = action.payload;
            const thread = ensureThread(state, threadKey);
            thread.activeQueryKey = queryKey;
        },

        markLoading(state, action) {
            const { threadKey, queryKey } = action.payload;
            const entry = ensureEntry(state, threadKey, queryKey);
            entry.status = "loading";
            entry.statusText = "질문을 ...";
            entry.markdown = "";
            entry.error = "";
            entry.updatedAt = Date.now();
        },

        markStreaming(state, action) {
            const { threadKey, queryKey } = action.payload;
            const entry = ensureEntry(state, threadKey, queryKey);
            entry.status = "streaming";
            entry.updatedAt = Date.now();
        },

        setStatusText(state, action) {
            const { threadKey, queryKey, statusText } = action.payload;
            const entry = ensureEntry(state, threadKey, queryKey);
            entry.statusText = statusText || "";
            entry.updatedAt = Date.now();
        },

        appendDelta(state, action) {
            const { threadKey, queryKey, chunk } = action.payload;
            const entry = ensureEntry(state, threadKey, queryKey);
            entry.status = "streaming";
            entry.markdown += chunk || "";
            entry.updatedAt = Date.now();
        },

        setFinal(state, action) {
            const { threadKey, queryKey, markdown } = action.payload;
            const entry = ensureEntry(state, threadKey, queryKey);
            entry.status = "done";
            entry.statusText = "Completed";
            entry.markdown = markdown || "";
            entry.updatedAt = Date.now();
        },

        setError(state, action) {
            const { threadKey, queryKey, error } = action.payload;
            const entry = ensureEntry(state, threadKey, queryKey);
            entry.status = "error";
            entry.statusText = "An error occurred";
            entry.error = error || "error";
            entry.updatedAt = Date.now();
        },

        clearThread(state, action) {
            const { threadKey } = action.payload;
            delete state.threads[threadKey];
        },
    },
});

export const {
    setActiveQuery,
    markLoading,
    markStreaming,
    setStatusText,
    appendDelta,
    setFinal,
    setError,
    clearThread,
} = aiCacheSlice.actions;

export default aiCacheSlice.reducer;
